import type { KeySetting } from '../types'

type KeyIdentityInput = {
  type?: unknown
  code?: unknown
  confIndex?: unknown
  keywords?: string
}

/**
 * 解析 number/string（支持 0x 前缀）为 number。
 *
 * @param value 待解析值
 * @returns 解析后的 number；无法解析时返回 undefined
 */
export const parseNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value
  if (typeof value !== 'string' || value === '') return undefined
  const parsed = Number.parseInt(value, 0)
  return Number.isNaN(parsed) ? undefined : parsed
}

/**
 * 统一解析 code 字段，兼容单值与数组值。
 *
 * @param value code 原始值
 * @returns 解析后的 code；无法解析时返回 undefined
 */
const resolveCodeValue = (value: unknown): number | undefined => {
  if (Array.isArray(value)) return parseNumber(value[0])
  return parseNumber(value)
}

/**
 * 归一化按键标识字段，便于比较。
 *
 * @param key 按键标识输入
 * @returns 归一化后的 type/code/confIndex
 */
const normalizeKeyIdentity = (key?: KeyIdentityInput | null) => ({
  type: parseNumber(key?.type),
  code: resolveCodeValue(key?.code),
  confIndex: parseNumber(key?.confIndex),
})

/**
 * 判断两个按键标识是否代表同一物理键。
 *
 * @param left 左侧按键标识
 * @param right 右侧按键标识
 * @returns 是否相同
 */
export const isSameKeyIdentity = (
  left?: KeyIdentityInput | null,
  right?: KeyIdentityInput | null,
): boolean => {
  const leftIdentity = normalizeKeyIdentity(left)
  const rightIdentity = normalizeKeyIdentity(right)

  if (
    leftIdentity.type == null ||
    leftIdentity.code == null ||
    rightIdentity.type == null ||
    rightIdentity.code == null
  ) {
    return false
  }

  if (leftIdentity.type !== rightIdentity.type || leftIdentity.code !== rightIdentity.code) {
    return false
  }

  if (leftIdentity.confIndex != null || rightIdentity.confIndex != null) {
    return leftIdentity.confIndex === rightIdentity.confIndex
  }

  return true
}

/**
 * 按 keywords 查找目标 setting。
 *
 * @param settings 当前层 keySettings
 * @param targetKey 目标键标识
 * @returns 命中的 setting；未命中返回 undefined
 */
export const findSettingByTargetKey = (
  settings: KeySetting[],
  targetKey?: KeyIdentityInput | null,
): KeySetting | undefined => {
  const targetKeywords = targetKey?.keywords
  if (!targetKeywords) return undefined
  return settings.find((setting) => setting.keywords === targetKeywords)
}

/**
 * 按 keywords 查找目标 setting 下标。
 *
 * @param settings 当前层 keySettings
 * @param targetKey 目标键标识
 * @returns 命中下标；未命中返回 -1
 */
export const findSettingIndexByTargetKey = (
  settings: KeySetting[],
  targetKey?: KeyIdentityInput | null,
): number => {
  const targetKeywords = targetKey?.keywords
  if (!targetKeywords) return -1
  return settings.findIndex((setting) => setting.keywords === targetKeywords)
}
